/**
 * The brief: the case for someone picking it up cold, and the first view.
 * A headline built from data (the client, the report type, the case's own
 * subject and how many others, when it's due); the case agent's last
 * statement — what it did, what is outstanding, what it would do next, and
 * what it read; the run queued or running, or Continue; spend against the
 * threshold the case runs under; what Start still needs, and Start; the
 * checklist, to add to, settle or trim; the open asks, each opening in
 * Needs you; Release where a draft waits. "Show more" opens who the
 * subjects are and the client's own request.
 */

import { memo, useState } from 'react';

import { Alert, Anchor, Badge, Button, Card, Group, Spoiler, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlayerPlay } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { CopilotText, type CaseLink } from '@/components/Copilot';
import { timeAgo } from '@/components/Inbox/timeAgo';
import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import {
  isSettled,
  SECTION_NAMES,
  type BriefSubject,
  type ChecklistStatus,
  type WorkbenchBrief,
  type WorkbenchHeader,
  type WorkbenchSpend,
} from '@/types/workbench';
import { toError } from '@/utils/errors';
import { AgentText } from './AgentText';
import { briefHeadline } from './briefHeadline';
import { ChecklistPanel } from './ChecklistPanel';
import { formatUsd } from './format';
import { StartButton } from './StartButton';

/** Ties shown before the rest fold away. */
const TIES_SHOWN = 8;

function Heading({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
      {children}
    </Text>
  );
}

function settled(checks: Record<string, number>): [number, number] {
  let done = 0;
  let total = 0;
  for (const [status, n] of Object.entries(checks)) {
    total += n;
    if (isSettled(status as ChecklistStatus)) {
      done += n;
    }
  }
  return [done, total];
}

/** Every tie, the first eight shown and the rest a click away. */
function Ties({ subject }: Readonly<{ subject: BriefSubject }>) {
  const [all, setAll] = useState(false);
  const shown = all ? subject.ties : subject.ties.slice(0, TIES_SHOWN);
  const more = subject.ties.length - shown.length;
  return (
    <Text size="xs" c="dimmed" data-testid={`brief-ties-${subject.id}`}>
      People and companies research found connected with them: {shown.join(', ')}
      {more > 0 && (
        <>
          {' '}
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={() => setAll(true)}
            data-testid={`brief-ties-more-${subject.id}`}
          >
            and {more} more
          </Anchor>
        </>
      )}
    </Text>
  );
}

function Subject({ subject }: Readonly<{ subject: BriefSubject }>) {
  const [done, total] = settled(subject.checklist);
  return (
    <Stack gap={2} data-testid={`brief-subject-${subject.id}`}>
      <Group gap={6} wrap="wrap">
        <Text size="sm" fw={600}>
          {subject.name}
        </Text>
        {subject.type && (
          <Badge size="xs" variant="outline" color="gray" style={{ textTransform: 'none' }}>
            {subject.type}
          </Badge>
        )}
        {subject.main && (
          <Badge size="xs" variant="light" color="blue" style={{ textTransform: 'none' }}>
            The case&apos;s own subject
          </Badge>
        )}
        {subject.unnamed && (
          <Badge size="xs" variant="light" color="orange" style={{ textTransform: 'none' }}>
            Not yet named
          </Badge>
        )}
      </Group>
      {subject.identifiers.length > 0 && (
        <Text size="xs" c="dimmed">
          {subject.identifiers.join(' · ')}
        </Text>
      )}
      {subject.ties.length > 0 && <Ties subject={subject} />}
      {total > 0 && (
        <Text size="xs" c="dimmed">
          {done} of {total} checks settled
        </Text>
      )}
    </Stack>
  );
}

/** What the client sent, in their own words. */
function ClientRequest({ request }: Readonly<{ request: WorkbenchBrief['request'] }>) {
  if (!request) {
    return (
      <Text size="xs" c="dimmed">
        No request on record.
      </Text>
    );
  }
  return (
    <>
      {request.subjectLine && (
        <Text size="sm" fw={600}>
          {request.subjectLine}
        </Text>
      )}
      {request.text ? (
        <Spoiler
          maxHeight={84}
          showLabel="Show the whole request"
          hideLabel="Show less"
          styles={{ control: { fontSize: 'var(--mantine-font-size-xs)' } }}
        >
          <Text size="xs" style={{ whiteSpace: 'pre-line' }} data-testid="case-brief-request">
            {request.text}
            {request.truncated ? ' …' : ''}
          </Text>
        </Spoiler>
      ) : (
        <Text size="xs" c="dimmed">
          The request has no text of its own.
        </Text>
      )}
    </>
  );
}

/** Spend against the threshold the case runs under — a threshold a person
 * raises, never a wall. */
export function spendLine(spend: WorkbenchSpend): string {
  const { threshold } = spend;
  const spent = formatUsd(threshold.spent) ?? '$0.00';
  const which = threshold.scope === 'before_start' ? 'the budget before Start' : 'the case budget';
  if (threshold.usd === null) {
    return `${spent} spent on the agents and the Assistant · no threshold set`;
  }
  return `${spent} of ${formatUsd(threshold.usd)} (${which})${
    Number(threshold.reserve) > 0
      ? ` · ${formatUsd(threshold.reserve)} kept for the conversation`
      : ''
  }`;
}

/** The last run's statement, as the case agent wrote it — the small
 * Markdown the Assistant writes, its links opening where they point. */
function Statement({
  brief,
  onOpenLink,
}: Readonly<{ brief: WorkbenchBrief; onOpenLink: (link: CaseLink) => void }>) {
  const { statement, run } = brief;
  return (
    <Stack gap={4} data-testid="brief-statement">
      <Group gap={6}>
        <Heading>Where the case stands</Heading>
        {run.status ? (
          <Badge size="xs" variant="light" color="blue" data-testid="brief-run">
            A run is {run.status}
            {run.reason ? ` (${run.reason.replace(/_/g, ' ')})` : ''}
          </Badge>
        ) : null}
      </Group>
      {statement ? (
        <>
          <div data-testid="brief-statement-text">
            <CopilotText text={statement.text} onOpenLink={onOpenLink} />
          </div>
          <Text size="xs" c="dimmed">
            The case agent, {timeAgo(statement.writtenAt)}
            {statement.costUsd ? ` · ${formatUsd(statement.costUsd)}` : ''}
            {statement.reads.length > 0 ? ` · read ${statement.reads.join(', ')}` : ''}
          </Text>
          {statement.missingReads.length > 0 ? (
            <Text size="xs" c="orange.8" data-testid="brief-missing-reads">
              The procedure asked it to read {statement.missingReads.join(', ')}, which it
              didn&apos;t.
            </Text>
          ) : null}
          {statement.halted === 'budget' ? (
            <Text size="xs" c="orange.8" data-testid="brief-halted">
              The run closed at the case&apos;s threshold: raise it in Needs you to carry on.
            </Text>
          ) : null}
        </>
      ) : (
        <Text size="xs" c="dimmed" data-testid="brief-no-statement">
          {run.status ? 'The first run is under way.' : 'No run has written a statement yet.'}
        </Text>
      )}
    </Stack>
  );
}

interface CaseBriefProps {
  header: WorkbenchHeader;
  brief: WorkbenchBrief | null;
  /** After an action here: the case refetches. */
  onChanged: () => void | Promise<void>;
  /** Open an ask in Needs you. */
  onOpenAsk: (askId: string) => void;
  /** Open one of the statement's links where it points (a record, beside
   * the view). */
  onOpenLink: (link: CaseLink) => void;
  /** Open a checklist item's records in the Evidence view. */
  onOpenRecords: (itemId: string) => void;
  /** Open a draft in the Document view (to release it). */
  onOpenDraft: (draftId: string) => void;
  /** Open Setup (a case set up from a request). */
  onOpenSetup?: () => void;
  /** The checklist item a link names. */
  focusedChecklistId?: string | null;
}

export const CaseBrief = memo(
  ({
    header,
    brief,
    onChanged,
    onOpenAsk,
    onOpenLink,
    onOpenRecords,
    onOpenDraft,
    onOpenSetup,
    focusedChecklistId = null,
  }: Readonly<CaseBriefProps>) => {
    const [more, setMore] = useState(false);
    const [continuing, setContinuing] = useState(false);
    const [notice, setNotice] = useState<{ color: string; text: string; askId?: string } | null>(
      null
    );
    if (!brief) {
      return null;
    }
    const headline = briefHeadline(header, brief.subjects, brief.reportTypeName);
    const { spend, start, actions } = brief;
    const atThreshold = spend.threshold.reached;

    const continueCase = async () => {
      setContinuing(true);
      setNotice(null);
      try {
        await vnextApiClient.continueCase(header.reportId);
        await onChanged();
      } catch (err) {
        if (err instanceof ApiConflictError && err.detail.reason === 'over_budget') {
          setNotice({
            color: 'orange',
            text: 'The case is at its threshold: raise it to continue.',
            askId: typeof err.detail.ask_id === 'string' ? err.detail.ask_id : undefined,
          });
          await onChanged();
        } else {
          Sentry.captureException(toError(err));
          notifications.show({ color: 'red', message: toError(err, 'Not continued').message });
        }
      } finally {
        setContinuing(false);
      }
    };

    return (
      <Card withBorder padding="sm" radius="md" data-testid="case-brief">
        <Stack gap="sm">
          {headline && (
            <Text size="sm" fw={600} data-testid="case-brief-headline">
              {headline}
            </Text>
          )}

          <Statement brief={brief} onOpenLink={onOpenLink} />

          <Group gap="xs" wrap="wrap" data-testid="brief-actions">
            {!start.started && onOpenSetup ? (
              <Button size="xs" variant="default" onClick={onOpenSetup} data-testid="brief-setup">
                Set up
              </Button>
            ) : null}
            {!start.started ? (
              <StartButton
                reportId={header.reportId}
                needs={start.needs}
                canStart={start.canStart}
                onStarted={onChanged}
              />
            ) : null}
            {actions.canContinue && !brief.run.status ? (
              <Button
                size="xs"
                variant="default"
                leftSection={<IconPlayerPlay size={14} />}
                loading={continuing}
                onClick={() => void continueCase()}
                data-testid="brief-continue"
              >
                Continue
              </Button>
            ) : null}
            {actions.releaseDraftId ? (
              <Button
                size="xs"
                variant={actions.releaseWaitsOn?.length ? 'default' : 'filled'}
                color="teal"
                onClick={() => onOpenDraft(actions.releaseDraftId ?? '')}
                data-testid="brief-release"
              >
                {actions.releaseWaitsOn?.length
                  ? `Release waits on ${actions.releaseWaitsOn.length}`
                  : 'Release the draft…'}
              </Button>
            ) : null}
            <Text size="xs" c={atThreshold ? 'orange.8' : 'dimmed'} data-testid="brief-spend">
              {spendLine(spend)}
              {atThreshold ? ' · at the threshold' : ''}
            </Text>
          </Group>
          {!start.started && start.needs.length > 0 ? (
            <Text size="xs" c="dimmed" data-testid="brief-start-needs">
              Start still needs: {start.needs.join('; ')}.
            </Text>
          ) : null}
          {notice ? (
            <Alert
              color={notice.color}
              variant="light"
              withCloseButton
              onClose={() => setNotice(null)}
              data-testid="brief-notice"
            >
              {notice.text}
              {notice.askId ? (
                <>
                  {' '}
                  <Anchor
                    size="sm"
                    component="button"
                    type="button"
                    onClick={() => notice.askId && onOpenAsk(notice.askId)}
                  >
                    Open the budget ask
                  </Anchor>
                </>
              ) : null}
            </Alert>
          ) : null}

          <ChecklistPanel
            reportId={header.reportId}
            items={brief.checklist}
            subjects={header.subjects}
            editable
            focusedId={focusedChecklistId}
            onChanged={onChanged}
            onOpenRecords={onOpenRecords}
          />

          {brief.asks.length > 0 ? (
            <Stack gap={4} data-testid="brief-asks">
              <Heading>Needs you</Heading>
              {brief.asks.map((ask) => (
                <Group key={ask.id} gap={6} wrap="nowrap" align="flex-start">
                  <Badge
                    size="xs"
                    variant="light"
                    color="orange"
                    style={{ textTransform: 'none', flex: 'none' }}
                  >
                    {SECTION_NAMES[ask.section]}
                  </Badge>
                  <Anchor
                    component="button"
                    type="button"
                    size="xs"
                    style={{ textAlign: 'left' }}
                    onClick={() => onOpenAsk(ask.id)}
                    data-testid={`brief-ask-${ask.id}`}
                  >
                    <AgentText text={ask.question ?? 'An ask'} />
                  </Anchor>
                  {ask.blocking ? (
                    <Badge size="xs" variant="light" color="red" style={{ textTransform: 'none' }}>
                      holding up a run
                    </Badge>
                  ) : null}
                </Group>
              ))}
            </Stack>
          ) : null}

          <Group justify="flex-end">
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={() => setMore(!more)}
              data-testid="case-brief-more"
            >
              {more ? 'Show less' : 'Show more'}
            </Anchor>
          </Group>
          {more && (
            <Stack gap="sm" pt={4} data-testid="case-brief-details">
              {brief.subjects.length > 0 && (
                <Stack gap={6}>
                  <Heading>Subjects</Heading>
                  {brief.subjects.map((subject) => (
                    <Subject key={subject.id} subject={subject} />
                  ))}
                </Stack>
              )}
              <Stack gap={4}>
                <Heading>The client’s request</Heading>
                <ClientRequest request={brief.request} />
              </Stack>
            </Stack>
          )}
        </Stack>
      </Card>
    );
  }
);
